export type ScriptType = "p2pkh" | "p2sh-p2wpkh" | "p2wpkh" | "p2tr";

export interface PathTemplate {
  purpose: 44 | 49 | 84 | 86;
  scriptType: ScriptType;
  label: string;
}

export const STANDARD_PATH_TEMPLATES: PathTemplate[] = [
  { purpose: 44, scriptType: "p2pkh", label: "Legacy (BIP-44)" },
  { purpose: 49, scriptType: "p2sh-p2wpkh", label: "Nested SegWit (BIP-49)" },
  { purpose: 84, scriptType: "p2wpkh", label: "Native SegWit (BIP-84)" },
  { purpose: 86, scriptType: "p2tr", label: "Taproot (BIP-86)" },
];

export function buildPath(purpose: number, account: number, change: number, index: number): string {
  return `m/${purpose}'/0'/${account}'/${change}/${index}`;
}

export interface PathRangeOptions {
  maxAccount?: number; // inclusive, default 0 (almost all wallets only ever use account 0)
  maxIndex?: number; // inclusive
}

/**
 * Enumerates (purpose, account, change, index) combinations to try during
 * derivation-path discovery. Bounded — the caller must check
 * estimateDerivationDiscoverySpace before running this over anything large.
 */
export function* enumerateStandardPaths(
  options: PathRangeOptions = {}
): Generator<{ path: string; scriptType: ScriptType }> {
  const maxAccount = options.maxAccount ?? 0;
  const maxIndex = options.maxIndex ?? 20;

  for (const template of STANDARD_PATH_TEMPLATES) {
    for (let account = 0; account <= maxAccount; account++) {
      for (const change of [0, 1]) {
        for (let index = 0; index <= maxIndex; index++) {
          yield {
            path: buildPath(template.purpose, account, change, index),
            scriptType: template.scriptType,
          };
        }
      }
    }
  }
}

export function countStandardPathCombinations(options: PathRangeOptions = {}): number {
  const maxAccount = options.maxAccount ?? 0;
  const maxIndex = options.maxIndex ?? 20;
  return STANDARD_PATH_TEMPLATES.length * (maxAccount + 1) * 2 * (maxIndex + 1);
}
